import { useState } from "react";
import type { Survey } from "../survey/schema";
import type { Answer } from "../state/persistence";

/**
 * 後半の確認フォーム (spec 2026-08-04 D4)。**ここで初めて文字になる。**
 *
 * 回答欄は最初から入力欄として出す — 「修正」を押してから直す形にすると、直せることが
 * 見て分からない。音声が取った値と人が直した値の差が、そのまま STT 精度の一次データに
 * なる (spec §3.2)。
 *
 * 編集状態はこの component の中に閉じてある。1 文字ごとに store を更新すると、
 * `answers` の変化が `useNarration.enqueue` まで波及して無駄な再レンダーが起きる。
 *
 * 感想 (D4) を survey 定義に入れないのが要点 (D6) — 入れると 6 問目として前半の
 * 音声パートで読み上げられてしまい、「フォームで書く」ことに意味がある設問でなくなる。
 */
export type ReviewSubmission = {
  /** 設問 id → 確定値 (空文字は未回答) */
  values: Record<string, string>;
  /** 体験の感想。空文字なら書かなかった */
  feedback: string;
};

type Props = {
  survey: Survey;
  answers: Record<string, Answer>;
  onSubmit: (submission: ReviewSubmission) => void;
};

const FEEDBACK_LABEL = "このアンケートはいかがでしたか（任意）";

function initialValue(a: Answer | undefined): string {
  if (!a || a.value === null) return "";
  return String(a.value);
}

export function ReviewForm({ survey, answers, onSubmit }: Props) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(survey.questions.map((q) => [q.id, initialValue(answers[q.id])]))
  );
  const [feedback, setFeedback] = useState("");

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
      <div className="px-4 py-4 flex flex-col gap-4">
        <div>
          <h1 className="text-[15px] font-bold text-slate-900">ご回答の確認</h1>
          <p className="mt-1 text-[13px] text-slate-600">
            音声で伺った内容です。違っているところは直してください。
          </p>
        </div>

        {survey.questions.map((q) => {
          const wasEmpty = initialValue(answers[q.id]) === "";
          return (
            <div key={q.id} className="flex flex-col gap-1">
              <label
                htmlFor={`answer-${q.id}`}
                className="text-[13px] font-bold text-slate-700 leading-relaxed"
              >
                {q.text}
              </label>
              <textarea
                id={`answer-${q.id}`}
                value={values[q.id] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [q.id]: e.target.value }))}
                rows={q.type === "short" ? 1 : 3}
                /* text-base (16px) は iOS Safari が focus 時に画面を強制ズームしない下限。
                   ここは全員が通る主動線なので 14px には落とさない。 */
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base text-slate-900 min-h-[44px] focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
              {wasEmpty && (
                // 警告色は使わない (完了画面と同じ考え方)。答えられなかったことを
                // 責めるのではなく、ここで書けることだけを伝える。
                <span className="text-[11px] text-slate-500">
                  音声では答えなかった設問です。ここで書けます。
                </span>
              )}
            </div>
          );
        })}

        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
          <span className="flex-1 h-px bg-slate-200" />
          ここから、このアンケートについて
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="review-feedback" className="text-[13px] font-bold text-slate-700">
            {FEEDBACK_LABEL}
          </label>
          <textarea
            id="review-feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="気づいたことを自由にお書きください"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base text-slate-900 min-h-[44px] focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          />
        </div>

        {/* min-h-[44px] は Apple HIG のタップ領域 */}
        <button
          type="button"
          onClick={() => onSubmit({ values, feedback: feedback.trim() })}
          className="mt-2 min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
        >
          この内容で送信
        </button>
      </div>
    </div>
  );
}
